/** Split SOA table rows so no single flex row exceeds one PDF page. */

import {
  SOA_MAX_SINGLE_ROW_HEIGHT_PT,
  SOA_TABLE_HEADER_HEIGHT_PT,
  estimateSoaRowHeightPt,
} from './pdfTablePagination';
import { isSoaSectionBannerRow } from './soaTableExportModel';

/** Printable height budget for one SOA data row (pt). */
export const SOA_ROW_PAGE_BUDGET_PT = SOA_MAX_SINGLE_ROW_HEIGHT_PT;

const CELL_PAD_PT = 6;

function estimateCharsPerLine(colWidthPt, fontSizePt) {
  const inner = Math.max(8, Number(colWidthPt) - CELL_PAD_PT);
  const fs = Math.max(6, Number(fontSizePt) || 11);
  return Math.max(1, Math.floor(inner / (fs * 0.52)));
}

function estimateLinesPerPage(fontSizePt) {
  const fs = Math.max(6, Number(fontSizePt) || 11);
  const lineHeight = fs * 1.35;
  return Math.max(1, Math.floor(SOA_ROW_PAGE_BUDGET_PT / lineHeight));
}

/**
 * Split long cell text into chunks that fit within one page row.
 */
export function splitCellTextForPdfPage(text, colWidthPt, fontSizePt) {
  const s = String(text ?? '').trim();
  if (!s) return [''];

  const charsPerLine = estimateCharsPerLine(colWidthPt, fontSizePt);
  const linesPerPage = estimateLinesPerPage(fontSizePt);
  const maxChars = Math.max(
    charsPerLine,
    Math.floor(charsPerLine * linesPerPage * 0.85),
  );

  if (s.length <= maxChars) return [s];

  const chunks = [];
  let remaining = s;
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining);
      break;
    }
    let cut = maxChars;
    const space = remaining.lastIndexOf(' ', cut);
    if (space > cut * 0.4) {
      cut = space;
    } else {
      const nl = remaining.lastIndexOf('\n', cut);
      if (nl > cut * 0.4) cut = nl;
    }
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  return chunks.length ? chunks : [''];
}

/**
 * Expand one SOA row into multiple render rows when any cell text would exceed a page.
 */
export function expandSoaRowForPdf(rowData, header, colWidths, fonts) {
  if (!header?.length) return [rowData];

  const lastCol = header.length - 1;
  const chunksByCol = header.map((key, colIdx) => {
    const fontSize =
      colIdx === 0
        ? fonts.bodyPt
        : colIdx === lastCol
          ? fonts.footnotePt
          : fonts.visitBodyPt;
    return splitCellTextForPdfPage(rowData[key], colWidths[colIdx], fontSize);
  });

  const partCount = Math.max(1, ...chunksByCol.map((c) => c.length));
  if (partCount === 1) return [rowData];

  const parts = [];
  for (let i = 0; i < partCount; i += 1) {
    const part = { ...rowData };
    let hasText = false;
    header.forEach((key, colIdx) => {
      const chunk = chunksByCol[colIdx][i] ?? '';
      part[key] = chunk;
      if (chunk) hasText = true;
    });
    if (!hasText && i > 0) continue;
    if (i > 0) part._soaContinuation = true;
    parts.push(part);
  }
  return parts.length ? parts : [rowData];
}

/**
 * Expand only rows that would exceed one landscape page; keep section banners intact.
 */
export function prepareSoaRowsForPdf(rows, header, colWidths, fonts) {
  if (!Array.isArray(rows) || !header?.length) return rows || [];

  return rows.flatMap((rowData) => {
    if (isSoaSectionBannerRow(rowData, header)) {
      return [rowData];
    }

    const estimatedHeight = estimateSoaRowHeightPt(rowData, header, colWidths, fonts);
    if (estimatedHeight >= SOA_ROW_PAGE_BUDGET_PT - 4) {
      return expandSoaRowForPdf(rowData, header, colWidths, fonts);
    }

    return [rowData];
  });
}
